import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Plus, X } from "lucide-react";
import { getClient } from "@/lib/clientsApi";
import { getPartner } from "@/lib/partnersApi";
import { NumericInput } from "@/components/ui/numeric-input";
import { DateInput } from "@/components/ui/date-input";
import { ClientInfoCard } from "@/components/clients/ClientInfoCard";

interface GeneralInfoStepProps {
  data: any;
  onChange: (data: any) => void;
  userRole?: string;
  readOnly?: boolean;
}

const GeneralInfoStep = ({ data, onChange, userRole, readOnly }: GeneralInfoStepProps) => {
  const isReadOnly = !!readOnly;
  const [clientInfo, setClientInfo] = useState<any | null>(data.clients || null);
  const [partnerInfo, setPartnerInfo] = useState<any | null>(null);
  const [tripLegs, setTripLegs] = useState<any[]>(data.tripLegs || []);
  const [passengerCount, setPassengerCount] = useState({
    adults: data.passengerCount?.adults || 0,
    children: data.passengerCount?.children || 0,
    infants: data.passengerCount?.infants || 0
  });
  const lastInternalPassengerCountSignatureRef = useRef<string>("");
  const lastInternalTripLegsSignatureRef = useRef<string>("");

  useEffect(() => {
    const snapshotClient = data.clients;
    if (snapshotClient && (snapshotClient.id || snapshotClient.first_name || snapshotClient.firstName)) {
      setClientInfo(snapshotClient);
      return;
    }
    if (!data.client_id) {
      setClientInfo(null);
      return;
    }

    let cancelled = false;
    getClient(Number(data.client_id))
      .then((client) => {
        if (!cancelled) {
          setClientInfo(client);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setClientInfo(null);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [data.client_id, data.clients]);

  const rawPartnerId = clientInfo?.partner_id ?? clientInfo?.partnerId;
  const partnerId = typeof rawPartnerId === "number" ? rawPartnerId : Number(rawPartnerId);

  useEffect(() => {
    if (!Number.isFinite(partnerId)) {
      setPartnerInfo(null);
      return;
    }

    let cancelled = false;
    getPartner(partnerId)
      .then((partner) => {
        if (!cancelled) {
          setPartnerInfo(partner);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setPartnerInfo(null);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [partnerId]);

  useEffect(() => {
    const incomingPassengerCount = {
      adults: data.passengerCount?.adults ?? 0,
      children: data.passengerCount?.children ?? 0,
      infants: data.passengerCount?.infants ?? 0,
    };
    const incomingPassengerCountSignature = JSON.stringify(incomingPassengerCount);
    if (incomingPassengerCountSignature !== lastInternalPassengerCountSignatureRef.current) {
      setPassengerCount(incomingPassengerCount);
    }

    const incomingTripLegs = Array.isArray(data.tripLegs) ? data.tripLegs : [];
    const incomingTripLegsSignature = JSON.stringify(
      incomingTripLegs.map((leg: any) => ({
        from_location: (leg?.from_location || "").toString(),
        to_location: (leg?.to_location || "").toString(),
        start_date: (leg?.start_date || "").toString(),
        end_date: (leg?.end_date || "").toString(),
      }))
    );
    if (incomingTripLegsSignature !== lastInternalTripLegsSignatureRef.current) {
      setTripLegs(incomingTripLegs);
    }
  }, [data.passengerCount, data.tripLegs]);


  const updatePassengerCount = async (type: 'adults' | 'children' | 'infants', value: number) => {
    if (isReadOnly) return;
    const safeValue = Math.max(0, value);
    const newCount = { ...passengerCount, [type]: safeValue };
    setPassengerCount(newCount);
    lastInternalPassengerCountSignatureRef.current = JSON.stringify(newCount);
    onChange({ ...data, passengerCount: newCount, detailedPassengerMode: false });
    
  };

  const addTripLeg = () => {
    if (isReadOnly) return;
    setTripLegs([
      ...tripLegs,
      { from_location: "", to_location: "", start_date: "", end_date: "" },
    ]);
  };

  const removeTripLeg = (index: number) => {
    if (isReadOnly) return;
    const newTripLegs = tripLegs.filter((_, i) => i !== index);
    setTripLegs(newTripLegs);
    lastInternalTripLegsSignatureRef.current = JSON.stringify(
      newTripLegs.map((leg: any) => ({
        from_location: (leg?.from_location || "").toString(),
        to_location: (leg?.to_location || "").toString(),
        start_date: (leg?.start_date || "").toString(),
        end_date: (leg?.end_date || "").toString(),
      }))
    );
    onChange({ ...data, tripLegs: newTripLegs });
  };

  const updateTripLeg = (index: number, field: string, value: string) => {
    if (isReadOnly) return;
    const newTripLegs = [...tripLegs];
    newTripLegs[index] = { ...newTripLegs[index], [field]: value };
    setTripLegs(newTripLegs);
    lastInternalTripLegsSignatureRef.current = JSON.stringify(
      newTripLegs.map((leg: any) => ({
        from_location: (leg?.from_location || "").toString(),
        to_location: (leg?.to_location || "").toString(),
        start_date: (leg?.start_date || "").toString(),
        end_date: (leg?.end_date || "").toString(),
      }))
    );
    onChange({ ...data, tripLegs: newTripLegs });
  };

  const isBusinessCategory = data.proposal_category === "business";
  const updateProposalCategory = (value: "individual" | "family" | "business") => {
    if (isReadOnly) return;
    onChange({
      ...data,
      proposal_category: value,
      sector: value === "business" ? (data.sector ?? null) : null,
    });
  };

  return (
    <div className="space-y-8">
      <div className="grid gap-5">
        <div>
          <Label htmlFor="title">Proposal Title</Label>
          <Input
            id="title"
            value={data.title || ""}
            onChange={(e) => onChange({ ...data, title: e.target.value })}
            disabled={isReadOnly}
          />
        </div>

        <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
          <div>
            <Label htmlFor="main_destination">Main Destination</Label>
            <Input
              id="main_destination"
              value={data.main_destination || ""}
              onChange={(e) => onChange({ ...data, main_destination: e.target.value })}
              disabled={isReadOnly}
            />
          </div>

          <div>
            <Label htmlFor="internal_reference">Internal Reference</Label>
            {/* Auto-generated on creation (REF-MM-YY-NNNN) and read-only — never editable. */}
            <Input
              id="internal_reference"
              value={data.internal_reference || ""}
              readOnly
              disabled
              placeholder="Auto-generated"
            />
            <p className="mt-1 text-xs text-muted-foreground">Automatically generated — cannot be edited.</p>
          </div>

          <div>
            <Label htmlFor="priority">Priority</Label>
            <Select
              value={(data.priority || "medium").toString().toLowerCase()}
              onValueChange={(value) => onChange({ ...data, priority: value })}
              disabled={isReadOnly}
            >
              <SelectTrigger id="priority">
                <SelectValue placeholder="Select priority" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="high">High</SelectItem>
                <SelectItem value="medium">Medium</SelectItem>
                <SelectItem value="low">Low</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="proposal_category">Proposal Category</Label>
            <Select
              value={data.proposal_category || undefined}
              onValueChange={(value) => updateProposalCategory(value as "individual" | "family" | "business")}
              disabled={isReadOnly}
            >
              <SelectTrigger id="proposal_category">
                <SelectValue placeholder="Select proposal category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="individual">Individual</SelectItem>
                <SelectItem value="family">Family</SelectItem>
                <SelectItem value="business">Business</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {isBusinessCategory && (
            <div className="md:col-span-2">
              <Label htmlFor="sector">Sector</Label>
              <Select
                value={data.sector || undefined}
                onValueChange={(value) => onChange({ ...data, sector: value })}
                disabled={isReadOnly}
              >
                <SelectTrigger id="sector">
                  <SelectValue placeholder="Select sector" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="tourism">Tourism</SelectItem>
                  <SelectItem value="hotels">Hotels</SelectItem>
                  <SelectItem value="transport">Transport</SelectItem>
                  <SelectItem value="events">Events</SelectItem>
                  <SelectItem value="esg">ESG</SelectItem>
                  <SelectItem value="sports">Sports</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
        </div>

        <div>
          <ClientInfoCard
            client={clientInfo}
            partnerName={partnerInfo?.name}
            partnerBusinessType={partnerInfo?.business_type ?? partnerInfo?.businessType}
            showPlaceholder
          />
        </div>

        <div>
          <Label htmlFor="context_observations">Context / Observations</Label>
          <textarea
            id="context_observations"
            value={data.context_observations || ""}
            onChange={(e) => onChange({ ...data, context_observations: e.target.value })}
            className="w-full min-h-[100px] px-3 py-2 text-sm rounded-md border border-input bg-background"
            placeholder="Add any relevant context or client history..."
            disabled={isReadOnly}
          />
        </div>
      </div>

      <div className="space-y-5 border-t pt-6">
        <div>
          <h3 className="text-lg font-semibold">Passengers</h3>
          <p className="text-sm text-muted-foreground mt-1">Specify the number of passengers by type</p>
        </div>
        <div className="grid grid-cols-3 gap-5">
          <div>
            <Label htmlFor="adults">Adults</Label>
            <NumericInput
              id="adults"
              value={passengerCount.adults}
              onChange={(value) => {
                updatePassengerCount('adults', parseInt(value) || 0);
              }}
              allowDecimals={false}
              className="mt-1"
              disabled={isReadOnly}
            />
          </div>
          <div>
            <Label htmlFor="children">Children</Label>
            <NumericInput
              id="children"
              value={passengerCount.children}
              onChange={(value) => {
                updatePassengerCount('children', parseInt(value) || 0);
              }}
              allowDecimals={false}
              className="mt-1"
              disabled={isReadOnly}
            />
          </div>
          <div>
            <Label htmlFor="infants">Infants</Label>
            <NumericInput
              id="infants"
              value={passengerCount.infants}
              onChange={(value) => {
                updatePassengerCount('infants', parseInt(value) || 0);
              }}
              allowDecimals={false}
              className="mt-1"
              disabled={isReadOnly}
            />
          </div>
        </div>
      </div>

      <div className="space-y-6 border-t pt-6">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold">Trip Legs</h3>
            <p className="text-sm text-muted-foreground mt-1">
              Define the different legs of the trip
            </p>
          </div>
          <Button type="button" size="sm" onClick={addTripLeg} disabled={isReadOnly}>
            <Plus className="mr-2 h-4 w-4" />
            Add Trip Leg
          </Button>
        </div>

        {tripLegs.length === 0 ? (
          <div
            className={`border-2 border-dashed rounded-lg p-12 text-center ${isReadOnly ? "" : "cursor-pointer transition-colors hover:bg-muted/30"}`}
            onClick={isReadOnly ? undefined : addTripLeg}
            onKeyDown={isReadOnly ? undefined : (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                addTripLeg();
              }
            }}
            role={isReadOnly ? undefined : "button"}
            tabIndex={isReadOnly ? -1 : 0}
            aria-label={isReadOnly ? undefined : "Add trip leg"}
          >
            <div className="flex flex-col items-center gap-3">
              <div className="rounded-full bg-muted p-3">
                <Plus className="h-6 w-6 text-muted-foreground" />
              </div>
              <div>
                <p className="text-sm font-medium">No trip legs added yet</p>
                <p className="text-sm text-muted-foreground mt-1">
                  Click "Add Trip Leg" to get started
                </p>
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-5">
            {tripLegs.map((leg, index) => (
              <div key={index} className="grid gap-5 p-6 border rounded-lg bg-card relative shadow-sm hover:shadow-md transition-shadow">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute top-3 right-3 h-8 w-8 p-0"
                  onClick={() => removeTripLeg(index)}
                  disabled={isReadOnly}
                >
                  <X className="h-4 w-4" />
                </Button>

                <div className="grid grid-cols-2 gap-5">
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">From Location</Label>
                    <Input
                      value={leg.from_location}
                      onChange={(e) => updateTripLeg(index, "from_location", e.target.value)}
                      placeholder="e.g., Dubai"
                      disabled={isReadOnly}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">To Location</Label>
                    <Input
                      value={leg.to_location}
                      onChange={(e) => updateTripLeg(index, "to_location", e.target.value)}
                      placeholder="e.g., Abu Dhabi"
                      disabled={isReadOnly}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Start Date</Label>
                    <DateInput
                      value={leg.start_date}
                      onChange={(value) => updateTripLeg(index, "start_date", value)}
                      disabled={isReadOnly}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">End Date</Label>
                    <DateInput
                      value={leg.end_date}
                      onChange={(value) => updateTripLeg(index, "end_date", value)}
                      disabled={isReadOnly}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default GeneralInfoStep;
